/**
 * Centralised toast notification messages.
 *
 * Rules:
 *  - Simple string  → message-only toast  (pass as `message:` prop)
 *  - Object         → toast with header / duration / placement
 *                     (spread into showToast: `{ type, ...TOAST_MESSAGES.FOO }`)
 *  - Function       → dynamic message, call with arguments before passing
 *
 * Adding a new notification:
 *  1. Add the constant here in the appropriate section.
 *  2. Use the constant in the component — never inline the string.
 */

/** Subset of toast props that live in this map (everything except `type` and `id`). */
export type ToastMessageOptions = {
  message: string;
  header?: string;
  duration?: number;
  placement?: "top-center" | "top-right" | "bottom-right";
};

// ─────────────────────────────────────────────────────────────────────────────
// Auth
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Fallback for a failed login-link request when the server gives no usable
 * reason (a network drop, a 500, an unexpected shape).
 *
 * It lists the causes rather than saying "try again", because retrying fixes
 * none of them — the old copy sent people round the same loop. When the server
 * DOES name the reason (no such account / suspended / unverified) the login
 * page shows that instead, so this is only the genuinely-unknown case.
 */
export const LOGIN_SEND_ERROR =
  "We couldn't send your login link. You may not have an account yet / your account may be suspended / your email may not be verified. Please check the address you entered, or contact support.";

/** Shown when the server reports the account is suspended (403). */
export const LOGIN_SUSPENDED =
  "Your account has been suspended. Please contact support to restore access.";

/** Shown for a 400 — the row exists but the email was never confirmed. */
export const LOGIN_UNVERIFIED =
  "Your email address isn't verified yet. Please check your inbox for the verification link, or contact support.";

/** Shown for a 404. The server's own wording there is "User not found", which
 *  is internal phrasing, so it is deliberately not passed through. */
export const LOGIN_NO_ACCOUNT =
  "We couldn't find an account for that email address. Please check the address, or sign up to get started.";
export const LOGIN_FAILED = "Login failed. Please check your email address or verify your account.";
export const SESSION_EXPIRED = "Your session has expired. Please log in again.";
export const LOGGED_OUT = "You have been logged out.";
export const AUTH_FAILED = "Authentication failed. Please try again.";

export const LOGIN_RESEND_ERROR: ToastMessageOptions = {
  header: "Error",
  message: "We couldn't resend the email. Please try again.",
};

// ─────────────────────────────────────────────────────────────────────────────
// Signup
// ─────────────────────────────────────────────────────────────────────────────
export const SIGNUP_FAILED = "We couldn't create your account. Please try again.";
export const SIGNUP_SAVE_ERROR = "We couldn't save your progress. Please try again.";

// ─────────────────────────────────────────────────────────────────────────────
// Verify email
// ─────────────────────────────────────────────────────────────────────────────
export const VERIFY_EMAIL_RESEND_ERROR: ToastMessageOptions = {
  header: "Error",
  message: "We couldn't resend the verification email. Please try again.",
};

// ─────────────────────────────────────────────────────────────────────────────
// LinkedIn OAuth
// ─────────────────────────────────────────────────────────────────────────────
export const LINKEDIN_USER_EXISTS =
  "An account with this email already exists. Please log in instead.";
export const LINKEDIN_USER_NOT_FOUND =
  "No account found for this LinkedIn profile. Please sign up first.";
export const INVALID_ORG_CODE =
  "Invalid Group / Company Code. Please check the code, or leave it empty to continue.";
export const LINKEDIN_NO_EMAIL =
  "LinkedIn did not share your email address. Please ensure your LinkedIn account has a verified email and try again.";
export const LINKEDIN_TOKEN_FAILED =
  "LinkedIn authentication timed out or was rejected. Please try again.";
export const LINKEDIN_USERINFO_FAILED =
  "Could not retrieve your LinkedIn profile. Please try again.";
export const LINKEDIN_SESSION_EXPIRED =
  "The LinkedIn sign-in session expired or was invalid. Please try again.";
export const LINKEDIN_NOT_CONFIGURED =
  "LinkedIn sign-in is not available right now. Please use email/password instead.";
export const LINKEDIN_CANCELLED =
  "LinkedIn sign-in was cancelled. You can try again whenever you're ready.";
export const LINKEDIN_GENERIC_ERROR = "LinkedIn sign-in failed. Please try again.";
export const SIGNUP_WELCOME_MESSAGE =
  "Let's get your profile set up to match you with the right network and define your North Star.";

// ─────────────────────────────────────────────────────────────────────────────
// Profile
// ─────────────────────────────────────────────────────────────────────────────
export const PROFILE_LOAD_ERROR = "Failed to load profile. Please refresh and try again.";
export const PROFILE_SAVE_ERROR = "Something went wrong. Please try again or contact support.";
export const PROFILE_UPDATE_ERROR = "We couldn't save those changes. Please try again.";
export const SOCIAL_LINKS_SAVE_ERROR = "Your social links couldn't be updated. Please try again.";
export const WORKING_HOURS_SAVED = "Working hours saved successfully.";
export const EXPERT_CONTRIBUTION_SAVED = "Expert contribution updated.";
export const WORKING_HOURS_SAVE_ERROR = "Failed to save working hours. Please try again.";
export const WORKING_HOURS_NO_CHANGES = "No changes to save.";

// ─────────────────────────────────────────────────────────────────────────────
// Calendar
// ─────────────────────────────────────────────────────────────────────────────
export const CALENDAR_CONNECT_ERROR = (msg?: string) =>
  msg ?? "We couldn't connect your calendar. Please try again.";
export const CALENDAR_CONNECT_START_ERROR = "We couldn't start the connection. Please try again.";
export const CALENDAR_CONNECT_FAILED = "Connection failed. Please try again.";
export const CALENDAR_DISCONNECT_ERROR = "We couldn't disconnect your calendar. Please try again.";
export const CALENDAR_SELECTION_SAVE_ERROR = "We couldn't save your selection. Please try again.";
export const NO_CALENDAR_CONNECTED =
  "Connect your calendar in your profile to schedule this session.";

// ─────────────────────────────────────────────────────────────────────────────
// Sessions
// ─────────────────────────────────────────────────────────────────────────────
export const SESSION_SCHEDULE_ERROR = "This meeting time isn't available. Pick another slot.";
export const SESSION_ONE_ON_ONE_SCHEDULED = "1:1 session scheduled.";
export const SESSION_MARK_READY_ERROR = (msg?: string) => msg ?? "We couldn't confirm this session. Please try again.";
export const SESSION_NOT_FOUND = "We can't find this session. Please refresh the page.";
export const SESSION_NOT_FOUND_REFRESH = "We can't find this session. Please refresh the page.";
export const NO_MEETING_AVAILABLE = "No meetings scheduled.";
export const DRAFT_SAVE_ERROR = "We couldn't save your draft. Please try again.";
export const SESSION_PREP_DRAFT_SAVED = "Saved as draft.";
export const SESSION_PREP_MARKED_READY = "Marked as ready successfully.";

// ─────────────────────────────────────────────────────────────────────────────
// Invitations
// ─────────────────────────────────────────────────────────────────────────────
export const INVITE_ACCEPT_ERROR = "We couldn't process this invitation. Please try again.";
export const INVITE_DECLINE_ERROR = "We couldn't decline this invitation. Please try again.";
export const MAX_ADVISORS_REACHED = (max: number) =>
  `You can select a maximum of ${max} experts.`;
export const ADD_ADVISORS_LIMIT = (count: number) =>
  `You can add up to ${count} experts to replace those who declined.`;

// ─────────────────────────────────────────────────────────────────────────────
// PAB / Session context
// ─────────────────────────────────────────────────────────────────────────────
export const PAB_SESSION_LOAD_ERROR = "We couldn't load this session. Please refresh the page.";
export const GOALS_LOAD_ERROR = "Your goals aren't loading right now. Please refresh the page.";
export const PAB_ADVISORS_LOAD_ERROR =
  "We couldn't load your expert list. Please refresh the page.";
export const PAB_NO_ACTIVE_SESSION =
  "No active Personal Advisory Board session found. Please refresh the page.";
export const PAB_NO_ACTIVE_SESSION_FOUND = "No active session found";
export const PAB_ARCHIVED_SUCCESS = "Personal Advisory Board has been archived successfully";

// ─────────────────────────────────────────────────────────────────────────────
// Follow-up emails
// ─────────────────────────────────────────────────────────────────────────────
export const FOLLOWUP_ERROR = "We couldn't send your follow-up. Please try again.";
export const FOLLOWUP_EMAIL_ERROR = "We couldn't send that email. Please try again.";
export const OPENING_MESSAGE_THREAD = (name: string) => `Opening thread for ${name}.`;

// ─────────────────────────────────────────────────────────────────────────────
// Goals
// ─────────────────────────────────────────────────────────────────────────────
export const GOAL_UPDATE_ERROR = "Failed to update goal. Please try again.";
export const GOAL_ACHIEVE_ERROR = "We couldn't update your goal status. Please try again.";
export const TASK_TITLE_REQUIRED = "Please add a task title to continue.";
export const TASK_UPDATE_ERROR = "We couldn't update this task. Please try again.";

// ─────────────────────────────────────────────────────────────────────────────
// Dashboard
// ─────────────────────────────────────────────────────────────────────────────
export const DASHBOARD_LOAD_ERROR =
  "We're having trouble loading the full dashboard. Showing available data for now.";
export const DASHBOARD_STATS_LOAD_ERROR =
  "Your stats aren't loading. Please refresh to see your progress.";

// ─────────────────────────────────────────────────────────────────────────────
// Contact
// ─────────────────────────────────────────────────────────────────────────────
export const CONTACT_SUCCESS =
  "Thank you for your message! We'll get back to you within 24 hours.";
export const CONTACT_ERROR = "We couldn't send your message. Please try again.";

// ─────────────────────────────────────────────────────────────────────────────
// Admin / Impersonation
// ─────────────────────────────────────────────────────────────────────────────
export const IMPERSONATE_URL_COPIED = "Login URL copied to clipboard";
export const IMPERSONATE_COPY_FAILED = "Could not copy to clipboard";
export const IMPERSONATE_FAILED = "Failed to generate login URL.";

// ─────────────────────────────────────────────────────────────────────────────
// Account switching
// ─────────────────────────────────────────────────────────────────────────────
export const ACCOUNT_SWITCH_FAILED = "Session expired. Please sign in to this account again.";

// ─────────────────────────────────────────────────────────────────────────────
// General / System
// ─────────────────────────────────────────────────────────────────────────────
export const COPY_ERROR = "Failed to copy. Please try again.";
export const UNEXPECTED_ERROR =
  "We couldn't complete that action. Please try again or contact support.";
export const REQUEST_FAILED = "Request failed.";

export const DRAFT_SAVE_FAILED = "We couldn't save your draft. Please try again.";
export const SESSION_READY_FAILED = "We couldn't confirm this session. Please try again.";
